import { type MessageMeta, api } from '@here/api'
import { useService } from 'solid-services'
import { createEffect, createResource, createSignal, onMount } from 'solid-js'
import { useChannelsReads } from '../channelReads/state'
import { useMessageMeta } from '../messageMeta'
import {  handleGetGeneralChannels, handleGetSetupChannels } from './resources'

export function useChannels() {
	const { channelReadsResource } = useService(useChannelsReads)()
	const { messageMetaResource } = useService(useMessageMeta)()

	// const [channelReads, setChannelReads] = createStore<Record<string, ChannelReads>>({})
	const [unreadMessageCount, setUnreadMessageCount] = createSignal<
		Record<string, string | undefined>
	>({})
	const [activeName, setActive] = createSignal<string>('general')

	const [generalChannelsResource] = createResource(handleGetGeneralChannels, { initialValue: [] })
	const [setupChannelsResource] = createResource(handleGetSetupChannels, { initialValue: [] })

	const active = () =>
		[...generalChannelsResource(), ...setupChannelsResource()].find((channel) => channel.name === activeName())
	const activeId = () => active()?.id

	// Compute initial unread counts when reads and meta load
	createEffect(() => {
		if (activeId()) {
			setUnreadMessageCount((prev) => ({
				...prev,
				[activeId()!]: undefined,
			}))
		}
	})

	// Compute initial unread counts when reads and meta load
	createEffect(() => {
		const meta = messageMetaResource()
		const reads = channelReadsResource()

		if (reads && meta) {
			let unreadMap: Record<string, string | undefined> = {}

			// biome-ignore lint/complexity/noForEach: <explanation>
			reads.forEach((read) => {
				const mm = meta.find((m) => m.channel_id === read.channel_id)

				if (!mm?.last_message_at) return

				const unread = new Date(mm.last_message_at) > new Date(read.last_read_at)

				unreadMap = {
					...unreadMap,
					[read.channel_id]: unread && mm.last_message_at || undefined,
				}
			})

			setUnreadMessageCount(unreadMap)
		}
	})

	// TODO move this out of here
	// Realtime subscription to message_meta updates
	onMount(() => {
		api()
			.channel('message_meta_updates')
			.on<MessageMeta>(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'message_meta',
				},
				(payload) => {
					if (payload.eventType === 'INSERT') {
					}

					if (payload.eventType === 'UPDATE') {
						const updated = payload.new
						// const lastReadAt = channelReads[updated.channel_id].last_read_at
						// const unread =
						// 	lastReadAt && new Date(updated.last_message_at) > new Date(lastReadAt)
						if (updated.channel_id !== activeId()) {
							setUnreadMessageCount((prev) => ({
								...prev,
								[updated.channel_id]: updated.last_message_at,
							}))
						}
					}
				},
			)
			.subscribe()
	})

	return {
		activeName,
		activeId,
		setActive: (channelName?: string) => setActive(channelName ?? 'general'),
		unreadMessageCount,
		generalChannelsResource,
    setupChannelsResource
	}
}
